<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div class="p-6">
    <h1 class="text-2xl font-semibold mb-6 dark:text-white flex items-center gap-2">
      Add New Fortigate {{ isMultiSubmit ? '(Optional)' : ''}}
      <div class="relative group">
      <button
        type="button"
        class="hover:bg-transparent bg-transparent"
        @click="startTutorial"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </h1>
    <form @submit.prevent="submitForm" class="space-y-6 p-6 glass-card shadow-sm ">
      <div v-for="field in visibleFormFields" :key="field.key" class="flex flex-col">
        <label :for="field.key" class="mb-1 font-medium dark:text-white">{{
          field.label
        }}</label>
        <div v-if="field.type === 'searchableSelect'" class="relative">
          <div class="relative">
            <input
              :id="field.key + '-search'"
              v-model="siteSearch"
              type="text"
              :placeholder="getSelectedSiteName || 'Search sites...'"
              class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @focus="handleSiteFocus"
              @blur="handleSiteBlur"
              :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors[field.key]"/>
            <div 
              v-show="isSiteDropdownVisible" 
              class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 dark:text-gray-50 border border-gray-300 rounded shadow-lg z-10"
            >
              <div 
                v-for="site in filteredSites"
                :key="site.id"
                class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                @mousedown="selectSite(site)"
              >
                {{ site.name }}
              </div>
              <div 
                v-if="filteredSites.length === 0" 
                class="px-3 py-2 text-gray-500"
              >
                No site found
              </div>
            </div>
          </div>
        </div>
        <GlassSelect
          v-else-if="field.type === 'select'"
          :id="field.key"
          v-model="formData[field.key]"
          :options="field.options || []"
          placeholder="Select an option"
        />
        <input
          v-else
          :id="field.key"
          v-model="formData[field.key]"
          :type="field.type"
          @focus="clearFieldError(field.key)"
          :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
        <FieldError v-if="field.type !== 'searchableSelect' && field.type !== 'select'" :message="fieldErrors[field.key]"/>
      </div>

      <div class="flex justify-end space-x-2">
        <button v-if="!isMultiSubmit"
          type="button"
          @click="goBack"
          class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Cancel
        </button>
        <button v-if="isMultiSubmit"
          type="button"
          @click="skipMultiSubmit"
          class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Skip
        </button>
        <button
          id="submit-fortigate-btn"
          type="submit"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 "
        >
          Create Fortigate
        </button>
      </div>
    </form>
  </div>
</template>

<script>

import { createFortigate } from "@/services/fortigateService";
import { getSites } from "@/services/siteService";
import FieldError from "@/components/FieldError.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  emits: ['fortigate-submitted', 'fortigate-skipped'],
  props: {
    isMultiSubmit: {
      type: Boolean,
      default: false
    },
    newmultiSubmitSite: {
      type: Number
    },
    submittedSite: { 
      type: Object, 
      default: null 
    },
  },
  name: "AddFortigate",
  components : {
    FieldError,
    PageError,
    GlassSelect,
  },
  setup(){
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      error: "",
      errorCode: "",
      multiSubmitSite: "",
      fieldErrors: {},
      formData: {
        site: null,
        token: "",
        scrape_interval: 60,
        scrape_timeout: 30,
      },
      sites: [],
      siteSearch: "",
      isSiteDropdownVisible: false,
      addFortigateTutorialSteps: [
        {
          element: '#site-search',
          action: 'display',
          message: 'Search and select the site this Fortigate belongs to.'
        },
        {
          element: '#token',
          action: 'type',
          value: 'demo-token-12345',
          message: 'Enter the Fortigate API token.'
        },
        {
          element: '#scrape_interval',
          action: 'display',
          message: 'How often (in seconds) metrics are collected. Default is 60.'
        },
        {
          element: '#scrape_timeout',
          action: 'display',
          message: 'Timeout in seconds for each scrape. Default is 30.'
        },
        {
          element: '#submit-fortigate-btn',
          action: 'display',
          message: 'Click to create the Fortigate.'
        },
      ],
      formFields: [
        {
          key: "site",
          label: "Site",
          type: "searchableSelect",
          required: true,
          filterFunction: (item, search) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.id.toString().includes(search),
        },
        {
          key: "token",
          label: "Token",
          type: "text",
          required: true,
        },
        {
          key: "scrape_interval",
          label: "Scrape Interval (seconds)",
          type: "number",
          required: true,
          staffOnly: true,
        },
        {
          key: "scrape_timeout",
          label: "Scrape Timeout (seconds)",
          type: "number",
          required: true,
          staffOnly: true,
        },
      ],
    };
  },
  computed: {
    visibleFormFields() {
      return this.formFields.filter(f => !f.staffOnly || useStore.isStaff());
    },
    filteredSites() {
      if (!this.siteSearch) {
        // If in multi-step form, only show the specific site
        if (this.isMultiSubmit && this.newmultiSubmitSite) {
          return this.sites.filter(s => s.id === this.newmultiSubmitSite);
        }
        return this.sites;
      }
      
      // Apply search filter
      const filtered = this.sites.filter((site) =>
        site.name.toLowerCase().includes(this.siteSearch.toLowerCase()) ||
        site.id.toString().includes(this.siteSearch)
      );

      // If in multi-step form, filter to only the specific site
      if (this.isMultiSubmit && this.newmultiSubmitSite) {
        return filtered.filter(s => s.id === this.newmultiSubmitSite);
      }
      
      return filtered;
    },

    getSelectedSiteName() {
      if (!this.formData.site) return null;
      const selectedSite = this.sites.find(p => p.id === this.formData.site);
      return selectedSite ? `${selectedSite.name}` : null;
    }
  },

  async mounted() {
    try {
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to create Fortigate!";
        this.errorCode = "422!";
        return;
      }

      // If in multi-submit mode, auto-select populate with submitted site and skip fetching all sites
      if (this.isMultiSubmit && this.submittedSite) {
        this.sites = [this.submittedSite];
        this.selectSite(this.submittedSite);
        return;
      }
      // Fetch sites with schema name
      this.sites = await getSites(validation.schemaName); 
      
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  },
  methods: {
    async startTutorial() {
      const originalFormData = { ...this.formData };
      await this._start(this.addFortigateTutorialSteps, () => {
        this.formData = { ...originalFormData };
      });
    },
    goBack(){
      this.$router.push({
        name: 'Fortigates',
        query: this.tenant.addSchemaParam()
      });
    },
    async submitForm() {
      try {

        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
            this.error = "Malformed request. Failed to create Fortigate!";
            this.errorCode = "422!";
            return;
        }
        this.fieldErrors = {};

        const requiredFields = ['site', 'token'];
        if (useStore.isStaff()) requiredFields.push('scrape_interval', 'scrape_timeout');
        this.fieldErrors = this.validateFields(this.formData, requiredFields);
        if (Object.keys(this.fieldErrors).length > 0) return;

        const fortigateData = { ...this.formData };

        const newFortigate = await createFortigate(fortigateData, validation.schemaName);

        if (this.isMultiSubmit){
          this.$emit('fortigate-submitted', fortigateData)
        } else {
          this.$router.push({ name: "Fortigates" });;
        }

      } catch (error) {
        this.fieldErrors = {}; 
        const items = this.formFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });

        console.error("Error creating fortigate:", error);
      }
    },
    handleSiteFocus() {
      this.clearFieldError('site');
      this.isSiteDropdownVisible = true;
      // Clear search when focusing to show all sites
      this.siteSearch = "";
    },
    clearFieldError(fieldName) {
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    validateFields(fields, fieldsToCheck) {
      const errors = {};
      for (const fieldName of fieldsToCheck) {
        const value = fields[fieldName];
        if (!value || (typeof value === "string" && !value.trim()) || (value instanceof Array && value.length === 0)) {
          errors[fieldName] = "This field cannot be empty";
        }
      }
      return errors;
    },
    handleSiteBlur() {
      setTimeout(() => {
        this.isSiteDropdownVisible = false;
      }, 200);
    },
    selectSite(site) {
      this.formData.site = site.id;
      this.isSiteDropdownVisible = false;
      this.siteSearch = "";
    },
    skipMultiSubmit(){
      this.$emit('fortigate-skipped','skipped')
    }
  },
};
</script>